import { Button, MagellanPopup } from 'components/atoms';
import { FormInput } from 'components/molecules';
import { useFormikContext } from 'formik';
import { IchannelsLightList } from 'pages/private/statements/types';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useDropzone } from 'react-dropzone';
import toast from 'react-hot-toast';
import { useTranslation } from 'react-i18next';
import { PiBroom, PiFileCsvDuotone, PiNotepadBold } from 'react-icons/pi';
import Popup from 'reactjs-popup';
import { PopupActions } from 'reactjs-popup/dist/types';
import {
  useGetSalesChannelProviders,
  useGetSalesChannelsLightList,
} from 'services/salesChannel';
import { Provider, ProviderOption } from 'services/salesChannel/types';
import { useGetSettlementLightList } from 'services/settlementPeriods';
import {
  SettlementPeriod,
  SettlementPeriodOption,
} from 'services/settlementPeriods/types';
import { useGetStatementTemplateTypes } from 'services/enums';
import { useStoreState } from 'providers/StoreStateContext';
import { getDefaultCurrency } from 'store/state';
import { BUTTON_VARIANT } from 'types/components/button/button.interface';
import { getSettlementPeriodName } from 'utils/global/name.utils';

interface IPopupUploadFileProps {
  onAdd?: (certificate: any, actions: PopupActions) => void;
  trigger?: JSX.Element;
  isUploading: boolean;
  clearPopupValues: () => void;
  progressIndicator: number;
}

const PopupUploadFile = ({
  onAdd,
  trigger,
  isUploading,
  clearPopupValues,
  progressIndicator,
}: IPopupUploadFileProps) => {
  const popupRef = useRef<PopupActions>(null);
  const { setFieldValue, values } = useFormikContext<any>();
  const { t } = useTranslation();
  const [dropzoneKey, setDropzoneKey] = useState(0);

  const { data: channelsLightList } = useGetSalesChannelsLightList();
  const { data: providers, refetch } = useGetSalesChannelProviders(
    values.residenceCertificate?.residenceCertificate?.SalesChannelId
  );
  const { data: settlementPeriodId } = useGetSettlementLightList();
  const statementTemplateTypesEnum = useGetStatementTemplateTypes();
  const { getStatementTemplateTypesOptions, getCurrenciesOptions } =
    useStoreState();
  const applicationCurrency =
    getDefaultCurrency()?.currencyCode ??
    t('input.placeholder.select-default');
  const currencyOptions = getCurrenciesOptions().filter(
    option =>
      option.value != null &&
      option.value !== '' &&
      option.value !== 0 &&
      !!option.label?.trim()
  );

  const channelsLightListOptions =
    channelsLightList?.map((country: IchannelsLightList) => ({
      label: country.name,
      value: country.id,
    })) ?? [];

  const settlementPeriodIdOptions: SettlementPeriodOption[] =
    settlementPeriodId?.map((country: SettlementPeriod) => ({
      label: getSettlementPeriodName(country.settlementPeriodName) ?? '',
      value: country.id,
    })) ?? [];

  const providersOptions: ProviderOption[] = Array.isArray(providers)
    ? providers.map((elem: Provider) => ({
        value: elem.id,
        label: elem.name,
      }))
    : [];

  const statementTemplateTypesOptions = getStatementTemplateTypesOptions();

  const shouldShowTemplateTypeSelect = 
    !statementTemplateTypesEnum.isLoading && 
    statementTemplateTypesOptions.length > 1;

  const getAllowedExtension = useCallback((templateTypeId: string) => {
    const templateType = statementTemplateTypesEnum.data?.find(
      (template: any) => template.id === parseInt(templateTypeId)
    );
    
    return templateType?.allowedExtension?.replace('.', '') || 'csv';
  }, [statementTemplateTypesEnum.data]);

  const getAcceptTypes = useCallback((templateTypeId: string) => {
    const allowedExtension = getAllowedExtension(templateTypeId);
    
    return { [`.${allowedExtension}`]: [] } as const;
  }, [getAllowedExtension]);

  const selectedTemplateType = values.residenceCertificate?.templateType || '1';
  const acceptTypes = getAcceptTypes(selectedTemplateType);

  const { getRootProps, getInputProps } = useDropzone({
    multiple: false,
    noClick: true,
    accept: acceptTypes as any,
      onDrop: acceptedFiles => {
        const file = acceptedFiles[0];

        if (file) {
          const allowedExtension = getAllowedExtension(selectedTemplateType);
          const fileName = file.name.toLowerCase();
          const expectedExtension = `.${allowedExtension.toLowerCase()}`;

          if (!fileName.endsWith(expectedExtension)) {
            const templateName = statementTemplateTypesOptions.find(
              (option: any) => option.value.toString() === selectedTemplateType
            )?.label || 'Unknown';
            
            setFieldValue('residenceCertificate.settlementFile', undefined);
            setDropzoneKey(prev => prev + 1);
            
            return toast.error(
              t('formik-error.invalid-file-extension-template', {
                extension: allowedExtension,
                templateName: templateName
              })
            );
          }

          setFieldValue('residenceCertificate.settlementFile', file);
        }
      },
    onDropRejected: (fileRejections) => {
      setFieldValue('residenceCertificate.settlementFile', undefined);
    }
  });

  useEffect(() => {
    if (
      values.residenceCertificate.residenceCertificate.SalesChannelId !==
        undefined &&
      !!values.residenceCertificate.residenceCertificate.SalesChannelId.length
    ) {
      refetch();
    }
  }, [values.residenceCertificate.residenceCertificate.SalesChannelId, refetch]);

  useEffect(() => {
    if (values.residenceCertificate?.settlementFile) {
      const allowedExtension = getAllowedExtension(selectedTemplateType);
      const fileName = values.residenceCertificate.settlementFile.name.toLowerCase();
      const expectedExtension = `.${allowedExtension.toLowerCase()}`;
      
      if (!fileName.endsWith(expectedExtension)) {
        setFieldValue('residenceCertificate.settlementFile', undefined);
      }
    }
  }, [selectedTemplateType, values.residenceCertificate?.settlementFile, setFieldValue, getAllowedExtension]);

  const fileName =
    values.residenceCertificate?.settlementFile?.name.split('.')[0];
  const fileExtension =
    values.residenceCertificate?.settlementFile?.name.split('.')[1];

  const disabledClearValues =
    !values.residenceCertificate?.SalesChannelId &&
    !values.residenceCertificate?.residenceCertificate &&
    !values.residenceCertificate?.settlementFile;

  const disabledOnAdd =
    !values.residenceCertificate.residenceCertificate?.SalesChannelId ||
    !values.residenceCertificate.residenceCertificate?.SettlementPeriodId ||
    !values.residenceCertificate.residenceCertificate.providerId ||
    values.residenceCertificate?.settlementFile === undefined
      ? true
      : false;

  const onAddClick = () => {
    onAdd &&
      popupRef.current &&
      values.residenceCertificate &&
      onAdd(
        {
          SalesChannelId:
            values?.residenceCertificate?.residenceCertificate.SalesChannelId,
          SettlementPeriodId:
            values?.residenceCertificate?.residenceCertificate
              .SettlementPeriodId,
          File: values?.residenceCertificate?.settlementFile,
          ProviderId:
            values?.residenceCertificate.residenceCertificate.providerId,
          CurrencyId:
            typeof values?.residenceCertificate?.currencyId === 'number' &&
            values.residenceCertificate.currencyId > 0
              ? values.residenceCertificate.currencyId
              : null,
        },
        popupRef.current
      );
  };

  return (
    <Popup ref={popupRef} modal nested trigger={trigger}>
      <MagellanPopup {...getRootProps({ key: dropzoneKey })} className='p-5 rounded-md'>
        <MagellanPopup.Header className='justify-center w-80 mb-5'>
          <PiNotepadBold /> {t('statements.popup.header')}
        </MagellanPopup.Header>

        <FormInput.Select
          name='residenceCertificate.residenceCertificate.SalesChannelId'
          label={t('input.label.salesChannel')}
          options={channelsLightListOptions}
          autoFocus
          isAsync={true}
          onChange={value => {
            setFieldValue(
              `residenceCertificate.residenceCertificate.SalesChannelName`,
              value.label
            );
            setFieldValue(
              `residenceCertificate.residenceCertificate.providerId`,
              ''
            );
            setFieldValue(
              `residenceCertificate.residenceCertificate.providerName`,
              ''
            );
          }}
          value={{
            label:
              values.residenceCertificate.residenceCertificate.SalesChannelName,
            value:
              values.residenceCertificate.residenceCertificate.SalesChannelId,
          }}
        />

        <FormInput.Select
          name='residenceCertificate.residenceCertificate.providerId'
          label={t('input.label.provider')}
          options={providersOptions}
          value={{
            label:
              values.residenceCertificate.residenceCertificate.providerIdName,
            value: values.residenceCertificate.residenceCertificate.providerId,
          }}
          isAsync={true}
          onChange={value => {
            setFieldValue(
              `residenceCertificate.residenceCertificate.providerIdName`,
              value.label
            );
          }}
        />

        <FormInput.Select
          name='residenceCertificate.residenceCertificate.SettlementPeriodId'
          label={t('input.label.settlementPeriod')}
          options={settlementPeriodIdOptions}
          isAsync={true}
        />

        <FormInput.Select
          name='residenceCertificate.currencyId'
          label={t('input.label.currency')}
          options={currencyOptions}
          placeholder={applicationCurrency}
          isClearable
          onChange={option => {
            const rawValue = option?.value;
            const currencyId =
              typeof rawValue === 'number' && rawValue > 0 ? rawValue : null;
            setFieldValue('residenceCertificate.currencyId', currencyId);
          }}
        />

        {shouldShowTemplateTypeSelect && (
          <FormInput.Select
            name='residenceCertificate.templateType'
            label={t('input.label.templateType')}
            options={statementTemplateTypesOptions}
            value={{
              label: statementTemplateTypesOptions.find(
                (option: any) => option.value.toString() === values.residenceCertificate.templateType
              )?.label || '',
              value: values.residenceCertificate.templateType,
            }}
          />
        )}

        <Button
          type='button'
          fullWidth
          onClick={() => document.getElementById('file-input')?.click()}>
          <Button.Icon icon={<PiFileCsvDuotone />} />
          <Button.Text text={t('button.addFile')} />
        </Button>

        <input id='file-input' {...getInputProps()} className='hidden' />

        {values.residenceCertificate?.settlementFile && (
          <div className='file-preview-wrapper'>
            <p className='file-preview-title'>{t('statements.popup.file')}</p>
            <div className='file-preview'>
              <p className='file-preview-name' title={fileName}>
                {fileName}
              </p>
              <p className='file-preview-extension' title={`.${fileExtension}`}>
                .{fileExtension}
              </p>
            </div>
          </div>
        )}

        <MagellanPopup.BottomButtons>
          <Button
            isClear
            onlyIcon
            onClick={clearPopupValues}
            disabled={disabledClearValues}>
            <Button.Description description={t('button.clear')} />
            <Button.Icon icon={<PiBroom />} />
          </Button>
          <Button
            variant={BUTTON_VARIANT.submit}
            disabled={disabledOnAdd}
            onClick={onAddClick}
            isLoading={isUploading}>
            <Button.Text text={t('button.upload')} />
            <Button.Icon icon={<PiNotepadBold />} />
          </Button>
        </MagellanPopup.BottomButtons>

        {progressIndicator > 0 ? (
          <div
            className='w-full h-3 rounded-sm overflow-hidden flex items-center pr-2 pl-2'
            style={{ backgroundColor: 'var(--secondary)' }}>
            <div
              className='rounded-sm w-full h-2  font-medium flex items-center justify-center transition-all duration-300 ease-in-out'
              style={{
                width: `${progressIndicator}%`,
                backgroundColor: 'var(--primary)',
                color: '#fff',
                padding: '4px 0 ',
                fontSize: '8px',
              }}>
              {progressIndicator}%
            </div>
          </div>
        ) : (
          <div className='h-3'></div>
        )}
      </MagellanPopup>
    </Popup>
  );
};

export default PopupUploadFile;
